/**
 * Work the old boards raise and move, carried to the work order register.
 *
 *   wo.create  work a board raised: the map's and the plans' NEW WORK ORDER,
 *              RAISE WORK on the safety, assessment, project, airfield,
 *              systems and carousel boards, a finding's RAISE WO, a failed
 *              critical utility check.
 *   wo.status  a board moving work on: START, COMPLETE, REOPEN, the
 *              officer's CLOSE and CANCEL on the map, and the airfield work
 *              board's MOVE IT ON and CLOSE IT.
 *
 * The board keeps its own copy for instant feedback. A raised order is
 * stamped by the shell (saving → saved, with the register's id), and the
 * register's refill keeps it until the platform lists its own copy in its
 * place (merge.ts `keepLocal`). A move only goes for work the register holds:
 * an order fed from the platform (`platformId`), or one a board raised and
 * the platform took (`platform.id`). The sample's own orders stay on the page,
 * as they always did.
 *
 * The register's rules are the register's. A board knows five states and the
 * platform eight, so a board's move is walked through the platform's legal
 * steps (raised → approved → dispatched → in progress → completed); a move the
 * platform has no path for — completed work cancelled, in-progress work sent
 * back to raised — is refused in words and the board's copy is put back as it
 * was. Nothing is forced: no missing evidence allowed, no verification.
 *
 * Both writes are retryable. The create sends the record's key as client_id
 * and Idempotency-Key, so TRY AGAIN files one order; a move re-reads where the
 * order is, walks only what is left, and sends each step with its own key.
 *
 * Nothing here imports the demo domain: this module is in the entry chunk.
 */
import { PERMISSION } from "../../../api/dataSource";
import {
  STATUS_LABEL,
  legalTransitions,
  type CreateWorkOrderInput,
  type WorkOrderKind,
  type WorkOrderPriority,
  type WorkOrderStatus,
} from "../../../domain/workOrders";
import { t } from "../../../i18n";
import { registerWrite, type WriteAck, type WriteContext } from "../writes";

/** The five states the boards know (tables.ts `WO_STATES`). */
export type BoardStatus = "open" | "acknowledged" | "in_progress" | "awaiting" | "completed";

/** A board's work order, as far as a write reads it. */
export interface BoardOrder {
  id: string;
  assetId?: string | null;
  assetName?: string;
  type?: string;
  priority?: string;
  status?: string;
  notes?: string;
  crew?: string;
  domain?: string;
  before?: string[];
  after?: string[];
  /** how many of `before` / `after` already reached the register, so a retry sends none twice */
  beforeSent?: number;
  afterSent?: number;
  /** the register's id, on an order fed from the platform */
  platformId?: string;
  /** how the platform took it, on one a board raised (or the map dispatched) */
  platform?: { id?: string; state?: string; status?: string; number?: string; reference?: string };
  closed?: string;
  cancelled?: unknown;
  closedBy?: unknown;
  closeNote?: string;
  verified?: boolean;
}

export interface CreateExtra {
  /** why the work was raised; corrective when left out */
  kind?: WorkOrderKind;
  /** the asset tag it is against, when the board has a real one (not a domain name) */
  assetTag?: string | null;
  /** hours to attend, when the board set a target */
  slaHours?: number | null;
  /** where it was raised, in the record's language — "the Interactive Map" */
  from?: string;
  /** where the work is (airside, terminal, landside), when the board knows better than its domain */
  area?: string;
}

/** What a board's copy looked like before the move, to put back on a refusal. */
export type BoardSnapshot = Pick<BoardOrder, "status" | "closed" | "cancelled" | "closedBy" | "closeNote" | "verified">;

export interface MoveExtra {
  order: BoardOrder;
  /** where the board moved it */
  to: BoardStatus;
  /** a cancel: the board shows it closed and marked cancelled */
  cancel?: boolean;
  note?: string;
  before?: BoardSnapshot;
  /** the register's id; none when the order is not on the platform (yet) */
  platformId?: string | null;
  reference?: string;
  from?: string;
}

/* ── raising ─────────────────────────────────────────────────────────── */

const PRIORITY: Record<string, WorkOrderPriority> = { P1: 1, P2: 2, P3: 3, P4: 4 };

/** The board's domain → the register's area (workOrderClass.ts). */
export function areaOf(domain: string | undefined): string | undefined {
  if (domain === "airside" || domain === "airfield") return "airside";
  if (domain === "terminal" || domain === "facility") return "terminal";
  if (domain === "landside") return "landside";
  return undefined;
}

const clip = (s: string, n: number) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

/** A board's order as the register takes it. Pure, for the tests. */
export function orderBody(rec: BoardOrder, extra: CreateExtra | null, siteId: string, key: string): CreateWorkOrderInput {
  const ex = extra ?? {};
  const title = [rec.type, rec.assetName].map((x) => (x ?? "").trim()).filter(Boolean).join(" — ") || "Work order";
  const description = [
    (rec.notes ?? "").trim(),
    rec.crew?.trim() ? `Crew: ${rec.crew.trim()}` : "",
    ex.from ? `Raised on ${ex.from} as ${rec.id}.` : "",
  ]
    .filter(Boolean)
    .join("\n");
  const tag = typeof ex.assetTag === "string" ? ex.assetTag.trim() : "";
  const sla = Number(ex.slaHours);
  const area = ex.area || areaOf(rec.domain);
  return {
    siteId,
    title: clip(title, 200),
    kind: ex.kind ?? "corrective",
    priority: PRIORITY[rec.priority ?? ""] ?? 3,
    ...(description ? { description: clip(description, 4000) } : {}),
    ...(tag ? { assetTag: tag } : {}),
    ...(Number.isFinite(sla) && sla >= 1 ? { slaHours: Math.min(8760, Math.round(sla)) } : {}),
    ...(area ? { area } : {}),
    clientId: key,
  };
}

const message = (e: unknown) => (e instanceof Error ? e.message : String(e));

async function photoFile(dataUrl: string, name: string): Promise<File> {
  const blob = await (await fetch(dataUrl)).blob();
  return new File([blob], name, { type: blob.type || "image/jpeg" });
}

/** Send the photographs not sent yet. Returns why it stopped, or null. */
async function sendPhotos(ctx: WriteContext, id: string, rec: BoardOrder, phase: "before" | "after"): Promise<string | null> {
  const list = (phase === "before" ? rec.before : rec.after) ?? [];
  const field = phase === "before" ? "beforeSent" : "afterSent";
  try {
    for (let n = rec[field] ?? 0; n < list.length; n++) {
      await ctx.source.uploadWorkOrderPhoto(id, await photoFile(list[n], `${rec.id}-${phase}-${n + 1}.jpg`), phase);
      rec[field] = n + 1;
    }
    return null;
  } catch (e) {
    return message(e);
  }
}

const KEYS = (site: string) => [["work-orders"], ["work-order"], ["kpis", site], ["fleet-history-wo", site]];

registerWrite("wo.create", {
  permission: () => PERMISSION.workOrdersWrite,
  invalidate: KEYS,
  /* the record's key is the order's client_id and Idempotency-Key: TRY AGAIN files one order */
  retryable: true,
  async send(ctx, rec: BoardOrder, extra: CreateExtra | null): Promise<WriteAck> {
    const wo = await ctx.source.createWorkOrder(orderBody(rec, extra, ctx.siteId, ctx.key));
    const why = await sendPhotos(ctx, wo.id, rec, "before");
    return {
      id: wo.id,
      reference: wo.number,
      kind: "work_order",
      tone: why ? "warn" : "ok",
      text: [
        t("{ref} raised in the work order register.", { ref: wo.number }),
        why ? t("The photographs did not reach the work order: {why}", { why }) : "",
      ]
        .filter(Boolean)
        .join(" "),
    };
  },
});

/* ── moving ──────────────────────────────────────────────────────────── */

/** The register's states a board's state can stand for. */
const ACCEPT: Record<BoardStatus, readonly WorkOrderStatus[]> = {
  open: ["draft", "approved"],
  acknowledged: ["dispatched"],
  in_progress: ["in_progress"],
  awaiting: ["on_hold"],
  completed: ["completed", "verified"],
};

/** The register's state → the board's. */
export function boardStatusOf(s: WorkOrderStatus): BoardStatus {
  switch (s) {
    case "draft":
    case "approved":
      return "open";
    case "dispatched":
      return "acknowledged";
    case "in_progress":
      return "in_progress";
    case "on_hold":
      return "awaiting";
    default:
      return "completed";
  }
}

/** States a walk may pass through on the way: never a hold, a cancel or a sign-off. */
const PASS_THROUGH = new Set<WorkOrderStatus>(["draft", "approved", "dispatched", "in_progress", "completed"]);

/**
 * The register's steps from where an order is to where the board put it.
 * [] when it is already there; null when the register has no way there.
 * Pure, for the tests.
 */
export function stepsFor(from: WorkOrderStatus, to: BoardStatus, cancel = false): WorkOrderStatus[] | null {
  const accept: readonly WorkOrderStatus[] = cancel ? ["cancelled"] : ACCEPT[to];
  if (accept.includes(from)) return [];
  /* a cancel is one step or none: reopening finished work to cancel it would be forcing it */
  if (cancel) return legalTransitions(from).includes("cancelled") ? ["cancelled"] : null;
  /* finished work is only ever reopened, never walked on through a reopen */
  if (from === "completed" && to !== "open" && to !== "in_progress") return null;
  /* a completed order sent back to raised is the register's reopen: back in progress */
  const goal = new Set<WorkOrderStatus>(to === "open" && !cancel && from === "completed" ? ["in_progress"] : accept);
  const seen = new Set<WorkOrderStatus>([from]);
  let frontier: WorkOrderStatus[][] = [[from]];
  while (frontier.length) {
    const next: WorkOrderStatus[][] = [];
    for (const path of frontier) {
      for (const n of legalTransitions(path[path.length - 1])) {
        if (seen.has(n)) continue;
        if (goal.has(n)) return [...path.slice(1), n];
        if (!PASS_THROUGH.has(n)) continue;
        seen.add(n);
        next.push([...path, n]);
      }
    }
    frontier = next;
  }
  return null;
}

const said = (s: WorkOrderStatus) => t(STATUS_LABEL[s]).toLowerCase();

/** Where the board put it, in the words a refusal uses. */
const BOARD_WORD: Record<BoardStatus, string> = {
  open: "raised",
  acknowledged: "acknowledged",
  in_progress: "in progress",
  awaiting: "on hold",
  completed: "closed",
};

/** Put the board's copy back the way it was. */
function putBack(order: BoardOrder, before: BoardSnapshot | undefined): void {
  if (!before) return;
  for (const k of ["status", "closed", "cancelled", "closedBy", "closeNote", "verified"] as const) {
    if (before[k] === undefined) delete order[k];
    else (order as unknown as Record<string, unknown>)[k] = before[k];
  }
}

registerWrite("wo.status", {
  permission: () => PERMISSION.workOrdersWrite,
  invalidate: KEYS,
  /* it re-reads where the order is and walks only what is left, each step with its own key */
  retryable: true,
  async send(ctx, _rec, extra: MoveExtra | null): Promise<WriteAck> {
    if (!extra?.order) throw new Error(t("Nothing to move."));
    const { order } = extra;
    /* where the register has it, once read — a walk refused half way is shown where it stopped */
    let now: WorkOrderStatus | null = null;
    let moved = false;
    try {
      if (!extra.platformId) {
        throw new Error(t("This work order is not on the platform yet, so the change stays on this page."));
      }
      const id = extra.platformId;
      const cur = await ctx.source.getWorkOrder(id);
      now = cur.status;
      const reference = cur.number || extra.reference || order.id;
      const steps = stepsFor(cur.status, extra.to, extra.cancel);
      if (steps === null) {
        throw new Error(
          t("The register can't move {ref} from {from} to {to}.", {
            ref: reference,
            from: said(cur.status),
            to: extra.cancel ? said("cancelled") : t(BOARD_WORD[extra.to]),
          }),
        );
      }
      const from = extra.from ?? "a board";
      let warn = "";
      for (const [i, step] of steps.entries()) {
        const last = i === steps.length - 1;
        if (step === "completed") {
          const why = await sendPhotos(ctx, id, order, "after");
          if (why) warn = t("The photographs did not reach the work order: {why}", { why });
        }
        const note = last
          ? extra.note?.trim() || `Moved on ${from}.`
          : `On the way to ${STATUS_LABEL[steps[steps.length - 1]].toLowerCase()}, from ${from}.`;
        const after = await ctx.source.transitionWorkOrder(
          id,
          {
            to: step,
            note,
            ...(step === "on_hold" ? { onHoldReason: extra.note?.trim() || "Waiting on parts or a possession" } : {}),
          },
          `${ctx.key}:${step}`,
        );
        now = after.status;
        moved = true;
      }
      /* the board shows where the register put it: a reopen lands in progress,
         a completion with its after photograph may already be verified */
      if (!extra.cancel) order.status = boardStatusOf(now);
      if (order.platform && !order.platformId) order.platform.status = now;
      return {
        id,
        reference,
        kind: "work_order",
        tone: warn ? "warn" : "ok",
        text: [
          steps.length
            ? t("{ref} is now {status} in the work order register.", { ref: reference, status: said(now) })
            : t("{ref} was already {status} in the work order register.", { ref: reference, status: said(now) }),
          warn,
        ]
          .filter(Boolean)
          .join(" "),
      };
    } catch (e) {
      /* refused: the board shows what the register holds, not what was asked */
      putBack(order, extra.before);
      if (moved && now) {
        order.status = boardStatusOf(now);
        if (order.platform && !order.platformId) order.platform.status = now;
      }
      throw e;
    }
  },
});
